import Vue from 'vue';
import Vuex from 'vuex';
import jwtDecode from 'jwt-decode';
import { auth as authService } from './auth/AuthService';

import { Board } from '../../common/board';
import { Project } from '../../common/project';
import { Box, BoxStyle, BoxStyleDimension } from '../../common/box';
import { apolloClient } from './apollo';
import gql from 'graphql-tag';
import _ from 'lodash';

import * as EDIT_LOCK_OWNER_FRAGMENT_QUERY from 'src/graphql/EditLockOwnerFragment.graphql';
import * as FAVORITE_BOARDS_QUERY from 'src/graphql/FavoriteBoards.graphql';

declare var USE_AUTH;

export interface ViewCard {
  isOpen: boolean;
  name: string;
}

Vue.use(Vuex);

const projectLockCheckInterval = 1;

setInterval(async () => {
  try {
    const { currentProject } = store.state;

    if (currentProject.id === undefined) {
      return;
    }

    const query = gql`query {
      projectLockOwner(id: ${currentProject.id}) {
        name
        email
        user_id
      }
    }
    `;

    const res = await apolloClient.query<any>({
      query,
      fetchPolicy: 'network-only',
    });
    const lockOwner = res.data.projectLockOwner;
    store.commit('setProjectLockOwner', lockOwner);
  } catch (e) {

  }
}, projectLockCheckInterval * 1000);

const store = new Vuex.Store({
  strict: false,
  state: {
    error: null,
    currentProject: new Project(),
    projectLockOwner: null,
    isLoadingLockOwner: false,
    projectList: [],
    groups: [],
    currentGroupPermission: null,
    appMenuMini: true,
    centralViewCards: [
      {
        name: 'NotesCard',
        isOpen: false,
      },
      {
        name: 'BoardsCard',
        isOpen: true,
      },
      {
        name: 'BoxCard',
        isOpen: false,
      },
      {
        name: 'GraphsCard',
        isOpen: true,
      },
    ],
    auth: null,
    userEmail: '',
    boxUnlockDialog: false,
    developerMode: localStorage.getItem('developerMode') === 'true',
    outdatedSimulationParameters: false,
    features: [],
    favorite_board_names: [],
  },
  getters: {
    hasProject: state => {
      const p = state.currentProject;
      return p && p.id !== undefined;
    },
    hasProjectLock: state => {
      return state.projectLockOwner &&
        state.projectLockOwner.user_id === state.auth.idTokenDecoded.sub &&
        state.currentProject.id !== undefined;
    },
    isDeveloper: (state, getters) => {
      if (!USE_AUTH) {
        return true;
      }

      return getters.hasRole('Developer');
    },
    isAdmin: (state, getters) => {
      if (!USE_AUTH) {
        return true;
      }

      return getters.hasRole('Administrator');
    },
    hasRole: (state) => (role) => {
      if (!state.auth) {
        return false;
      }

      const authorization = state.auth.accessTokenDecoded['https://skalman.algoryx.se/user_authorization'];

      if (!authorization || authorization.roles == null) {
        return false;
      }

      return authorization.roles.includes(role);
    },
    authenticatedAndAuthorized: state => {
      if (!store.getters.authenticated) {
        return false;
      }

      const groups = state.auth.accessTokenDecoded.groups;
      return groups.length === 1;
    },
    authenticated: state => {
      if (!USE_AUTH) {
        return true;
      }

      if (!state.auth) {
        return false;
      }
      return new Date().getTime() < state.auth.expiresAt;
    },
    getMeshFileUrls: state => {
      return state.currentProject.boards.filter(board => board.visible && board.hasSimulationSuccess()).map(board => board.simulationResults[0].mesh_file_url);
    },
    getCard: (state) => (name) => {
      return state.centralViewCards.find(card => card.name === name);
    },
    hasSimulationSuccess: state => {
      let has = false;

      for (let i = 0; i < state.currentProject.boards.length; ++i) {
        if (state.currentProject.boards[i].hasSimulationSuccess()) {
          has = true;
          break;
        }
      }
      return has;
    },
    hasVisibleSimulationSuccess: state => {
      let has = false;

      for (let i = 0; i < state.currentProject.boards.length; ++i) {
        if (state.currentProject.boards[i].hasSimulationSuccess() && state.currentProject.boards[i].visible) {
          has = true;
          break;
        }
      }
      return has;
    },
    hasVisibleSimulationSuccessWithHumidityImpact: (state, getters) => {
      let has = false;

      if (!getters.hasHumidityImpactPermission)
        return false;

      for (let i = 0; i < state.currentProject.boards.length; ++i) {
        if (state.currentProject.boards[i].hasSimulationSuccessWithHumidityImpact() && state.currentProject.boards[i].visible) {
          has = true;
          break;
        }
      }
      return has;
    },
    hasVisibleSimulationSuccessWithCarbonFootprint: (state, getters) => {
      let has = false;

      if (!state.currentProject.simulate_carbon_footprint || !getters.hasCarbonFootprintPermission)
        return false;

      for (let i = 0; i < state.currentProject.boards.length; ++i) {
        if (state.currentProject.boards[i].hasSimulationSuccessWithCarbonFootprint() && state.currentProject.boards[i].visible) {
          has = true;
          break;
        }
      }
      return has;
    },
    hasVisibleSimulationSuccessWithFoodSafety: (state, getters) => {
      let has = false;

      if (!state.currentProject.simulate_food_safety || !getters.hasFoodSafetyPermission)
        return false;

      for (let i = 0; i < state.currentProject.boards.length; ++i) {
        if (state.currentProject.boards[i].hasSimulationSuccessWithFoodSafety() && state.currentProject.boards[i].visible) {
          has = true;
          break;
        }
      }
      return has;
    },
    anyActiveSimulation: state => {
      return state.currentProject.boards.some(board => board.activeSimulation);
    },
    anyShouldSimulate: state => {
      return state.currentProject.boards.some(board => !board.activeSimulation &&
        !board.hasSimulationSuccess() && board.enableSimulation);
    },
    anyHasBoardResults: state => {
      return state.currentProject.boards.some( board => board.visible && board.hasBoardResults() && !board.hasSimulationErrors() );
    },
    simulationIsValid: (state, getters) => {
      if (state.currentProject.boards.length == 0)
        return false;

      if (state.currentProject.simulate_carbon_footprint && getters.hasCarbonFootprintPermission) {
        const plant = state.currentProject.plant;
        if (plant == null || plant.deleted || plant.placeholder)
          return false;

        if (plant.newest != null)
          return false;
      }

      if (state.currentProject.simulate_food_safety && getters.hasFoodSafetyPermission) {
        if (state.currentProject.food_type         == null || state.currentProject.food_type.deleted        ||
            state.currentProject.packaging_system  == null || state.currentProject.packaging_system.deleted ||
            state.currentProject.storage_condition == null || state.currentProject.storage_condition.deleted)
          return false;

        if (state.currentProject.food_type.newest         != null ||
            state.currentProject.packaging_system.newest  != null ||
            state.currentProject.storage_condition.newest != null)
          return false;
      }

      return true;
    },
    simulationDisabledMessage: (state, getters) => {
      if (state.currentProject.boards.length == 0)
        return 'Create a board to simulate.';

      if (state.currentProject.simulate_carbon_footprint && getters.hasCarbonFootprintPermission) {
        const plant = state.currentProject.plant;
        if (plant == null || plant.deleted || plant.placeholder)
          return 'Select a destination to simulate.';

        if (plant.newest != null)
          return 'Update the destination to simulate.';
      }

      if (state.currentProject.simulate_food_safety && getters.hasFoodSafetyPermission) {
        if (state.currentProject.food_type         == null || state.currentProject.food_type.deleted        ||
            state.currentProject.packaging_system  == null || state.currentProject.packaging_system.deleted ||
            state.currentProject.storage_condition == null || state.currentProject.storage_condition.deleted)
          return 'Select all three food safety properties to simulate.';

        if (state.currentProject.food_type.newest         != null ||
            state.currentProject.packaging_system.newest  != null ||
            state.currentProject.storage_condition.newest != null)
          return 'Update the food safety properties to simulate.';
      }

      if (state.outdatedSimulationParameters)
        return 'Simulation results are outdated because of updated simulation parameters.';

      return '';
    },
    hasFeaturePermission: (state) => (name) => {
      return state.features.find( feature => feature.name === name ) != null;
    },
    hasHumidityImpactPermission: (state, getters) => {
      return getters.hasFeaturePermission('Humidity Impact');
    },
    hasCarbonFootprintPermission: (state, getters) => {
      return getters.hasFeaturePermission('Carbon Footprint');
    },
    hasFoodSafetyPermission: (state, getters) => {
      return getters.hasFeaturePermission('Food Safety');
    },
    hasFavoriteBoard: (state) => (name) => {
      if (name == null) {
        return false;
      }

      const favoriteBoards = state.favorite_board_names;
      const found = favoriteBoards.some( board => board.name.trim().toLowerCase() === name.trim().toLowerCase());
      return found;
    },
    hasBoardPerformanceIndexPermission: (state, getters) => {
      return getters.hasFeaturePermission('Board Performance Index');
    },
  },
  mutations: {
    async acquireProjectLock(state) {
      state.isLoadingLockOwner = true;

      const query = gql`mutation {
          lockProject(id: ${state.currentProject.id}) {
            name
            email
            user_id
          }
        }
      `;

      const res = await apolloClient.mutate({
        mutation: query,
      });
      const lockOwner = res.data.lockProject;

      apolloClient.writeFragment({
        id: `Project:${state.currentProject.id}`,
        fragment: EDIT_LOCK_OWNER_FRAGMENT_QUERY,
        data: {
          __typename: 'Project',
          editLockOwner: res.data.lockProject,
        },
      });

      store.commit('setProjectLockOwner', lockOwner);
      state.isLoadingLockOwner = false;
    },
    async releaseProjectLock(state) {
      if (state.currentProject.id === undefined) {
        return;
      }

      const query = gql`mutation {
          unlockProject(id: ${state.currentProject.id})
        }
      `;

      const res = await apolloClient.mutate({
        mutation: query,
      });

      apolloClient.writeFragment({
        id: `Project:${state.currentProject.id}`,
        fragment: EDIT_LOCK_OWNER_FRAGMENT_QUERY,
        data: {
          __typename: 'Project',
          editLockOwner: null,
        },
      });

      store.commit('setProjectLockOwner', null);
    },
    async deleteFavoriteBoard(state, board) {
      if (!board || board.id == null) {
        return;
      }

      return apolloClient.mutate({
        mutation: gql`mutation ($favoriteBoardId: Int!) {
            deleteFavoriteBoard(favoriteBoardId: $favoriteBoardId)
          }
        `,
        variables: {
          favoriteBoardId: board.id,
        },
        update: (store, {data: { deleteFavoriteBoard }}) => {
          let data: any = store.readQuery({
            query: FAVORITE_BOARDS_QUERY,
            variables: ( { group: null } ),
          });

          data.favorite_boards_for_group = data.favorite_boards_for_group.filter(
            board => board.id !== deleteFavoriteBoard
          );

          store.writeQuery({
            query: FAVORITE_BOARDS_QUERY,
            variables: ( { group: null } ),
            data,
          });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteFavoriteBoard: board.id
        },
        refetchQueries: [{ query: FAVORITE_BOARDS_QUERY, variables: ( { group: null } ) }],
      });
    },
    setProjectLockOwner(state, owner) {
      if (!state.projectLockOwner && !owner) {
        return;
      }

      if (!state.projectLockOwner && owner ||
          state.projectLockOwner && !owner ||
          state.projectLockOwner.user_id !== owner.user_id
      ) {
        console.log(`setProjectLockOwner`, owner);
        state.projectLockOwner = owner;
      }
    },
    setError(state, err) {
      state.error = err;
    },
    toggleDeveloperMode(state) {
      state.developerMode = !state.developerMode;
      localStorage.setItem('developerMode', state.developerMode.toString());
    },
    setBoxUnlockDialog(state, flag) {
      state.boxUnlockDialog = flag;
    },
    setCurrentProject(state, project: Project) {
      // console.log(`Set current project`, project);

      state.currentProject = project;
    },
    setFeatures(state, features: any[]) {
      state.features = features;
    },
    setFavoriteBoardNames(state, favorite_board_names: any[]) {
      state.favorite_board_names = favorite_board_names;
    },
    setProjects(state, projects: Project[]) {
      state.projectList = projects;
    },
    setGroups(state, groups: any[]) {
      state.groups = groups;
    },
    setAuth(state, { auth }) {
      state.auth = auth;

      if (!auth) {
        return;
      }

      // Make sure token has not expired
      if (new Date().getTime() > state.auth.expiresAt) {
        state.auth = null;
        return;
      }

      if (auth.accessToken) {
        auth.accessTokenDecoded = jwtDecode(auth.accessToken);

        // Flatten authorization
        const authorization = auth.accessTokenDecoded['https://skalman.algoryx.se/user_authorization'];

        if (!authorization) {
          auth.accessTokenDecoded.groups = [];
          auth.accessTokenDecoded.roles = [];
        } else {
          auth.accessTokenDecoded.groups = authorization.groups;
          auth.accessTokenDecoded.roles = authorization.roles;
        }

        authService.auth0.client.userInfo(auth.accessToken, (err, profile) => {
          if (profile) {
            state.userEmail = profile.email;
          }
        });
      }

      if (auth.idToken) {
        auth.idTokenDecoded = jwtDecode(auth.idToken);
      }
    },
    updateMeshFileUrl(state, pathInfo) {
      state.currentProject.boards[pathInfo.boardIndex].simulationResults[0].mesh_file_url = pathInfo.meshFileUrl;
    },
    meshFileUrlNotFound(state, url) {
      for (const board of state.currentProject.boards) {
        if (board.simulationResults.length > 0 && board.simulationResults[0].mesh_file_url === url) {
          board.simulationResults[0].bct = null;

          if (board.simulationResults[0].status === 0) {
            board.simulationResults[0].status = null;
          }
        }
      }
    },
    closeCardsButOne(state, card: ViewCard) {
      for (const cardInfo of state.centralViewCards) {
        cardInfo.isOpen = false;
      }

      card.isOpen = true;
    },
    toggleAppMenuMini(state) {
      state.appMenuMini = !state.appMenuMini;
    },
  },
  actions: {
    updateCard(context) {
      context.commit('updateCard');
    },
  },
});

export default store;



// WEBPACK FOOTER //
// ./src/store.ts